import { useSeason } from '../../context/SeasonContext';
import { useEffect, useMemo, useState } from 'react';
import { ModalPortal } from '../ModalPortal/ModalPortal';
import { updateDigitalTrace } from '../../api/artifacts';
import styles from './ArtifactModal.module.css';

const DEFAULT_HELP_TEXT = 'Добавьте ссылку на результат работы или кратко опишите его.';

export function ArtifactModal({ isOpen, onClose, onSaved, entry, contextId }) {
  const { canEdit } = useSeason();
  const [textAnswer, setTextAnswer] = useState('');
  const [answerUrl, setAnswerUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const digitalTrace = useMemo(() => entry?.digital_trace, [entry]);
  const description = useMemo(() => entry?.homework_description || DEFAULT_HELP_TEXT, [entry]);
  const templateUrl = entry?.homework_template_url || '';
  const entryId = contextId || entry?.entry_id;

  useEffect(() => {
    if (!isOpen || !digitalTrace) return;
    
    setTextAnswer(digitalTrace.text_answer || '');
    setAnswerUrl(digitalTrace.answer_url || '');
  }, [isOpen, digitalTrace]);

  if (!isOpen) return null;

  const isGraded = digitalTrace?.can_edit === false;
  const isEnabled = digitalTrace?.enabled !== false;

  const handleClose = () => {
    setError('');
    setSuccess('');
    setTextAnswer('');
    setAnswerUrl('');
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    const text = textAnswer.trim();
    const url = answerUrl.trim();

    if (!text && !url) {
      setError('Добавьте текст или ссылку на результат.');
      return;
    }

    if (isGraded) {
      setError('Задание уже оценено. Редактирование невозможно.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        text_answer: text || null,
        answer_url: url || null,
      };

      await updateDigitalTrace(entryId, payload);
      
      setSuccess('Цифровой след сохранён.');
      onSaved?.();
      setTimeout(handleClose, 800);
      
    } catch (err) {
      if (err?.status === 409) {
        setError('Задание уже оценено. Редактирование невозможно.');
      } else if (err?.status === 400) {
        setError('Необходимо заполнить хотя бы одно поле: текст или ссылку.');
      } else {
        setError(err?.message || 'Не удалось сохранить цифровой след.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isEnabled) {
    return (
      <ModalPortal onClose={handleClose}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h3>Цифровой след</h3>
            <button className={styles.closeBtn} type="button" onClick={handleClose}>×</button>
          </div>
          <div className={styles.modalScrollArea}>
            <p>Для этого задания цифровой след не предусмотрен.</p>
          </div>
        </div>
      </ModalPortal>
    );
  }

  return (
    <ModalPortal onClose={handleClose}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h3>Цифровой след</h3>
          <button className={styles.closeBtn} type="button" onClick={handleClose} disabled={loading}>
            ×
          </button>
        </div>
        
        <div className={styles.modalScrollArea}>
          <p className={styles.description}>{description}</p>
          
          {templateUrl ? (
            <a href={templateUrl} className={styles.templateLink} target="_blank" rel="noreferrer">
              Открыть шаблон
            </a>
          ) : null}

          {digitalTrace?.submission_id && (
            <div className={styles.submissionsBlock}>
              <div className={styles.submissionsHeader}>
                <strong>Ваша отправка</strong>
                {digitalTrace.status === 'PASSED' && <span className={`${styles.gradedBadge} ${styles.passed}`}>Зачтено</span>}
                {digitalTrace.status === 'FAILED' && <span className={`${styles.gradedBadge} ${styles.failed}`}>Не зачтено</span>}
                {digitalTrace.status === 'PENDING' && <span className={`${styles.gradedBadge} ${styles.pending}`}>На проверке</span>}
              </div>
              <div className={styles.submissionItem}>
                <div className={styles.submissionMeta}>
                  <span>
                    {new Date(digitalTrace.updated_at || digitalTrace.submitted_at).toLocaleString('ru-RU')}
                  </span>
                </div>
                {digitalTrace.text_answer && (
                  <p className={styles.submissionComment}>{digitalTrace.text_answer}</p>
                )}
                {digitalTrace.answer_url && (
                  <div className={styles.submissionLinks}>
                    <a href={digitalTrace.answer_url} target="_blank" rel="noreferrer">
                      Открыть ссылку
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {!isGraded && (
            <form className={styles.form} onSubmit={handleSubmit}>
              <label className={styles.label}>
                Ссылка на результат
                <input
                  type="url"
                  placeholder="https://..."
                  value={answerUrl}
                  onChange={(event) => setAnswerUrl(event.target.value)}
                  disabled={loading || isGraded || !canEdit}
                />
              </label>
              
              <label className={styles.label}>
                Комментарий
                <textarea
                  placeholder="Коротко опишите, что внутри."
                  value={textAnswer}
                  onChange={(event) => setTextAnswer(event.target.value)}
                  disabled={loading || isGraded || !canEdit}
                  rows={4}
                />
              </label>
              
              {error && <div className={styles.error}>{error}</div>}
              {success && <div className={styles.success}>{success}</div>}
              
              <button 
                className={styles.submitButton} 
                type="submit" 
                disabled={loading || isGraded || !canEdit}
                title={isGraded ? 'Задание уже оценено' : ''}
              >
                {loading ? 'Сохранение...' : 'Сохранить'}
              </button>
            </form>
          )}

          {isGraded && (
            <div className={styles.gradedMessage}>
              <p>
                {digitalTrace.status === 'PASSED' 
                  ? '✅ Задание зачтено' 
                  : digitalTrace.status === 'FAILED' 
                    ? '❌ Задание не зачтено' 
                    : 'Задание оценено'}
              </p>
              {digitalTrace.grade && <p><strong>Оценка:</strong> {digitalTrace.grade}</p>}
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
}
